import { inject, Injectable, signal } from '@angular/core';
import { toObservable } from '@angular/core/rxjs-interop';
import { Observable, of, catchError, distinctUntilChanged, lastValueFrom, switchMap, take, shareReplay } from 'rxjs';
import { PaymentProviderAccountApprovalUpdateRTM } from '@gains-com/realtime';
import { environment } from '../../../environments/environment.js';
import { HttpClient } from '@angular/common/http';
import { LOGGER_FACTORY } from '../logger/logger-factory.provider';
import { UserViewService } from './user-view.service.js';

@Injectable({
  providedIn: 'root',
})
export class PaymentProviderAccountsViewService {
  private readonly _http = inject(HttpClient);
  private readonly _logger = inject(LOGGER_FACTORY).create('PaymentProviderAccountsViewService');
  private readonly _userView = inject(UserViewService);

  /**
   * Backing state. Was a per-fetch `BehaviorSubject` (recreated + completed inside
   * the pipe below); now a single persistent signal. `_stateInitialized` preserves
   * the previous `this._state?.next()` semantics — imperative mutations are no-ops
   * until the first fetch has populated state (the old optional-chain on an undefined
   * subject silently dropped them).
   */
  private readonly _state = signal<PaymentProviderAccountApprovalUpdateRTM[] | null>(null);
  private _stateInitialized = false;
  private readonly _state$ = toObservable(this._state);
  private readonly _loading = signal<boolean>(true);

  private _fetchPaymentProviderAccounts$ = () => {
    return this._http
      .get<PaymentProviderAccountApprovalUpdateRTM[]>(`${environment.apiUrl}/payment-provider-accounts`)
      .pipe(
        take(1),
        catchError((err) => {
          this._logger.error('Failed to fetch payment provider accounts', { error: err });
          return of(null);
        }),
      );
  };

  paymentProviderAccounts$: Observable<PaymentProviderAccountApprovalUpdateRTM[] | null> = this._userView.state$.pipe(
    distinctUntilChanged((u1, u2) => u1?.user_id === u2?.user_id && u1?.pii_submitted === u2?.pii_submitted),
    switchMap((user) => {
      if (!user || !user.pii_submitted) {
        this._state.set(null);
        return of(null);
      }

      return this._fetchPaymentProviderAccounts$().pipe(
        switchMap((accounts) => {
          this._state.set(accounts ?? []);
          this._stateInitialized = true;
          return this._state$;
        }),
      );
    }),
    shareReplay(1),
  );

  /** Readonly signal surface for new consumers. */
  readonly loading = this._loading.asReadonly();

  /** Backward-compat stream shim for existing observable consumers. */
  loading$: Observable<boolean> = toObservable(this._loading);

  /**
   * Replaces the entire payment provider accounts array atomically
   * This method is optimized for full state replacements to prevent race conditions
   * where new subscriptions might get partially updated state from the cache.
   * Use this when you have the complete list of accounts (e.g., from API response).
   *
   * @param accounts Complete array of accounts to replace the current state
   */
  setPaymentProviderAccounts(accounts: PaymentProviderAccountApprovalUpdateRTM[]) {
    if (this._stateInitialized) {
      this._state.set(accounts);
    }
  }

  /**
   * Updates a single payment provider account from realtime events
   * This method processes a single account update to merge with existing state.
   * Use this for partial updates (e.g., realtime approval status changes).
   *
   * @param account The account to update or add
   */
  updatePaymentProviderAccount(account: PaymentProviderAccountApprovalUpdateRTM) {
    if (!this._stateInitialized) {
      return;
    }
    const currentAccounts = this._state() ?? [];
    const updatedAccounts = [
      ...currentAccounts.filter((a) => a.payment_provider_account_id !== account.payment_provider_account_id),
      account,
    ];
    this._state.set(updatedAccounts);
  }

  async syncPaymentProviderAccounts() {
    this._loading.set(true);
    try {
      const accounts = await lastValueFrom(this._fetchPaymentProviderAccounts$());
      if (this._stateInitialized) {
        this._state.set(accounts ?? []);
      }
    } catch (err) {
      this._logger.error('Failed to sync payment provider accounts', { error: err });
      throw err;
    } finally {
      this._loading.set(false);
    }
  }
}
